import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Swords, ChevronLeft, ChevronRight } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAM_LOGO = (abbr) => `https://a.espncdn.com/i/teamlogos/nfl/500/${abbr === 'LA' ? 'lar' : abbr === 'WAS' ? 'wsh' : abbr.toLowerCase()}.png`;

const fmtMl = (v) => {
  const n = parseFloat(v);
  if (!n) return '—';
  return n > 0 ? `+${Math.round(n)}` : `${Math.round(n)}`;
};

const NflScores = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState(null);

  const load = (w) => {
    setLoading(true);
    axios.get(`${API}/nfl/scores`, { params: w ? { week: w } : {} })
      .then(r => { setData(r.data); setWeek(r.data.week); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const games = data?.games || [];
  const byDay = games.reduce((acc, g) => {
    const k = `${g.weekday} · ${g.gameday}`;
    (acc[k] = acc[k] || []).push(g);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Swords className="h-8 w-8 text-brand-lime" />
                NFL Scores & Matchups
              </h1>
              <p className="text-muted-foreground mt-1">Live scores, spreads and totals</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="icon"
                className="border-[#1e3a5f] text-white hover:bg-[#112240]"
                disabled={!week || week <= 1}
                onClick={() => load(week - 1)}
                data-testid="nfl-week-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Badge variant="outline" className="border-brand-cyan text-brand-cyan px-4 py-2 text-sm" data-testid="nfl-week-badge">
                Week {week ?? '—'}
              </Badge>
              <Button
                variant="outline" size="icon"
                className="border-[#1e3a5f] text-white hover:bg-[#112240]"
                disabled={!week || (data?.max_week && week >= data.max_week)}
                onClick={() => load(week + 1)}
                data-testid="nfl-week-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">{[1, 2, 3, 4, 5].map(i => <div key={i} className="h-24 skeleton rounded-lg" />)}</div>
          ) : games.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]">
              <CardContent className="p-12 text-center text-muted-foreground" data-testid="nfl-no-games">No games found.</CardContent>
            </Card>
          ) : (
            Object.entries(byDay).map(([day, dayGames]) => (
              <div key={day} className="mb-6">
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2 px-1">{day}</div>
                <div className="space-y-3">
                  {dayGames.map((g, i) => (
                    <Card key={g.game_id} className="bg-[#0f1e36] border-[#1e3a5f] card-hover" data-testid={`nfl-game-card-${g.game_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex-1 min-w-[220px] space-y-2">
                            {[['away', g.away, g.away_score], ['home', g.home, g.home_score]].map(([side, abbr, score]) => (
                              <div key={side} className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <img src={TEAM_LOGO(abbr)} alt={abbr} className="w-7 h-7" onError={(e) => { e.target.style.display = 'none'; }} />
                                  <span className="font-semibold text-white">{abbr}{side === 'home' && <span className="text-muted-foreground text-xs ml-1">(H)</span>}</span>
                                </div>
                                <span className="font-mono text-xl font-bold text-white">{score ?? '-'}</span>
                              </div>
                            ))}
                          </div>
                          <div className="text-right space-y-1 min-w-[150px]">
                            <div className={`text-xs uppercase tracking-wide font-semibold ${g.state === 'in' ? 'text-brand-lime' : g.status === 'Final' || g.state === 'post' ? 'text-muted-foreground' : 'text-brand-cyan'}`}>
                              {g.status}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Spread <span className="text-white font-mono">{g.spread_line || '—'}</span> · O/U <span className="text-white font-mono">{g.total_line || '—'}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              ML <span className="text-white font-mono">{fmtMl(g.away_moneyline)}</span> / <span className="text-white font-mono">{fmtMl(g.home_moneyline)}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default NflScores;
